import type { Point2D } from '@shared/models/geometry.model';
import { getDistance } from '@shared/helpers/geometry/coordinates.helpers';
import { getPolygonCornerAngleDegrees } from './corner-angle-edit.helpers';

/** Horizontal input half-height plus clearance for the mid-edge point control. */
export const SEGMENT_LENGTH_LABEL_OFFSET_PX = 17;
const SEGMENT_LENGTH_LABEL_GAP_PX = 8;
const SEGMENT_LENGTH_INPUT_HEIGHT_PX = 18;
const SEGMENT_LENGTH_CHARACTER_WIDTH_PX = 7;
const SEGMENT_LENGTH_INPUT_HORIZONTAL_CHROME_PX = 6;
const CORNER_ANGLE_LABEL_OFFSET_PX = 28;

const isPointInsideScreenPolygon = (
  point: Point2D,
  polygon: readonly Point2D[],
): boolean => {
  let inside = false;
  for (
    let currentIndex = 0, previousIndex = polygon.length - 1;
    currentIndex < polygon.length;
    previousIndex = currentIndex, currentIndex += 1
  ) {
    const current = polygon[currentIndex];
    const previous = polygon[previousIndex];
    if (!current || !previous) continue;
    const crosses =
      current[1] > point[1] !== previous[1] > point[1] &&
      point[0] <
        ((previous[0] - current[0]) * (point[1] - current[1])) /
          (previous[1] - current[1]) +
          current[0];
    if (crosses) inside = !inside;
  }
  return inside;
};

/**
 * Resolve a selected edge from exactly two selected vertex indexes.
 * Adjacent indexes form a normal edge; first+last form the closing edge.
 */
export const resolveSelectedEdge = (
  pointCount: number,
  indexes: readonly number[],
): { startIndex: number; endIndex: number } | undefined => {
  if (indexes.length !== 2 || pointCount < 2) return undefined;
  const [first, second] = indexes;
  if (first === undefined || second === undefined) return undefined;
  if (first === second) return undefined;

  const lo = Math.min(first, second);
  const hi = Math.max(first, second);
  if (hi - lo === 1) return { startIndex: lo, endIndex: hi };

  // Closing edge of a polygon ring.
  if (lo === 0 && hi === pointCount - 1 && pointCount >= 3) {
    return { startIndex: hi, endIndex: 0 };
  }

  return undefined;
};

/**
 * Offset a segment length label from the midpoint along the screen-space normal
 * so it does not cover the mid-edge add-point control.
 * Prefers the side away from {@link shapeCentroidScreen} when provided.
 */
export const offsetSegmentLabelScreenPoint = (input: {
  midpoint: Point2D;
  start: Point2D;
  end: Point2D;
  offsetPx?: number;
  shapeCentroidScreen?: Point2D;
  polygonScreenPoints?: readonly Point2D[];
  preferInside?: boolean;
  labelBoxSize?: { width: number; height: number };
}): Point2D => {
  const dx = input.end[0] - input.start[0];
  const dy = input.end[1] - input.start[1];
  const length = Math.hypot(dx, dy);
  if (!(length > 1e-6)) {
    const offsetPx = input.offsetPx ?? SEGMENT_LENGTH_LABEL_OFFSET_PX;
    return [input.midpoint[0], input.midpoint[1] - offsetPx];
  }

  // Unit normal in screen space.
  let nx = -dy / length;
  let ny = dx / length;

  const polygon = input.polygonScreenPoints;
  if (polygon && polygon.length >= 3) {
    const signedAreaTwice = polygon.reduce((sum, current, index) => {
      const next = polygon[(index + 1) % polygon.length];
      if (!next) return sum;
      return sum + current[0] * next[1] - next[0] * current[1];
    }, 0);
    // Screen Y grows downward: positive area means the interior is on the
    // edge's left, so the outward normal is the right-hand normal.
    if (signedAreaTwice > 0) {
      nx = -nx;
      ny = -ny;
    }
  } else if (input.shapeCentroidScreen) {
    const centroid = input.shapeCentroidScreen;
    const awayX = input.midpoint[0] - centroid[0];
    const awayY = input.midpoint[1] - centroid[1];
    // Flip so the label sits outside the polygon when possible.
    if (nx * awayX + ny * awayY < 0) {
      nx = -nx;
      ny = -ny;
    }
  }

  // The label is axis-aligned while the segment may be diagonal. Offset by
  // the box's support distance along the segment normal, plus control clearance,
  // so a diagonal line cannot cut through a label corner.
  const offsetPx =
    input.offsetPx ??
    (input.labelBoxSize
      ? (Math.abs(nx) * input.labelBoxSize.width) / 2 +
        (Math.abs(ny) * input.labelBoxSize.height) / 2 +
        SEGMENT_LENGTH_LABEL_GAP_PX
      : SEGMENT_LENGTH_LABEL_OFFSET_PX);
  if (!(offsetPx > 0)) return input.midpoint;

  const outward: Point2D = [
    input.midpoint[0] + nx * offsetPx,
    input.midpoint[1] + ny * offsetPx,
  ];
  if (input.preferInside && polygon && input.labelBoxSize) {
    const inward: Point2D = [
      input.midpoint[0] - nx * offsetPx,
      input.midpoint[1] - ny * offsetPx,
    ];
    const halfWidth = input.labelBoxSize.width / 2;
    const halfHeight = input.labelBoxSize.height / 2;
    const corners: Point2D[] = [
      [inward[0] - halfWidth, inward[1] - halfHeight],
      [inward[0] + halfWidth, inward[1] - halfHeight],
      [inward[0] + halfWidth, inward[1] + halfHeight],
      [inward[0] - halfWidth, inward[1] + halfHeight],
    ];
    if (
      corners.every((corner) => isPointInsideScreenPolygon(corner, polygon))
    ) {
      return inward;
    }
  }

  return outward;
};

/** Refit an existing segment-label side using the rendered input box size. */
export const fitSegmentLabelScreenPoint = (input: {
  start: Point2D;
  end: Point2D;
  preferredPoint: Point2D;
  labelBoxSize: { width: number; height: number };
}): Point2D => {
  const midpoint: Point2D = [
    (input.start[0] + input.end[0]) / 2,
    (input.start[1] + input.end[1]) / 2,
  ];
  const dx = input.end[0] - input.start[0];
  const dy = input.end[1] - input.start[1];
  const length = Math.hypot(dx, dy);
  if (!(length > 1e-6)) return input.preferredPoint;

  let nx = -dy / length;
  let ny = dx / length;
  const preferredDx = input.preferredPoint[0] - midpoint[0];
  const preferredDy = input.preferredPoint[1] - midpoint[1];
  if (preferredDx * nx + preferredDy * ny < 0) {
    nx = -nx;
    ny = -ny;
  }
  const offset =
    (Math.abs(nx) * input.labelBoxSize.width) / 2 +
    (Math.abs(ny) * input.labelBoxSize.height) / 2 +
    SEGMENT_LENGTH_LABEL_GAP_PX;
  return [midpoint[0] + nx * offset, midpoint[1] + ny * offset];
};

export type SegmentLengthLabel = {
  startIndex: number;
  endIndex: number;
  lengthMeters: number;
  screenPoint: Point2D;
};

/**
 * Build one centered, outward-offset label anchor for every polygon segment.
 */
export const buildSegmentLengthLabels = (input: {
  metricPoints: readonly Point2D[];
  screenPoints: readonly Point2D[];
  offsetPx?: number;
  preferInside?: boolean;
}): SegmentLengthLabel[] => {
  if (
    input.metricPoints.length < 3 ||
    input.metricPoints.length !== input.screenPoints.length
  ) {
    return [];
  }

  return input.metricPoints.flatMap((start, startIndex) => {
    const endIndex = (startIndex + 1) % input.metricPoints.length;
    const end = input.metricPoints[endIndex];
    const startScreen = input.screenPoints[startIndex];
    const endScreen = input.screenPoints[endIndex];
    if (!end || !startScreen || !endScreen) return [];

    const lengthMeters = getDistance(start, end);
    if (!Number.isFinite(lengthMeters) || !(lengthMeters > 0)) return [];

    const midpoint: Point2D = [
      (startScreen[0] + endScreen[0]) / 2,
      (startScreen[1] + endScreen[1]) / 2,
    ];
    const labelText = String(Math.round(lengthMeters * 10) / 10);
    const inputBoxSize = {
      width:
        Math.max(2, labelText.length) * SEGMENT_LENGTH_CHARACTER_WIDTH_PX +
        SEGMENT_LENGTH_INPUT_HORIZONTAL_CHROME_PX,
      height: SEGMENT_LENGTH_INPUT_HEIGHT_PX,
    };
    return [
      {
        startIndex,
        endIndex,
        lengthMeters,
        screenPoint: offsetSegmentLabelScreenPoint({
          midpoint,
          start: startScreen,
          end: endScreen,
          offsetPx: input.offsetPx,
          polygonScreenPoints: input.screenPoints,
          preferInside: input.preferInside,
          labelBoxSize: inputBoxSize,
        }),
      },
    ];
  });
};

export type CornerAngleLabel = {
  pointIndex: number;
  angleDegrees: number;
  screenPoint: Point2D;
};

/** Build one outward-offset angle label at every polygon corner. */
export const buildCornerAngleLabels = (input: {
  metricPoints: readonly Point2D[];
  screenPoints: readonly Point2D[];
  offsetPx?: number;
}): CornerAngleLabel[] => {
  if (
    input.metricPoints.length < 3 ||
    input.metricPoints.length !== input.screenPoints.length
  ) {
    return [];
  }
  const offsetPx = input.offsetPx ?? CORNER_ANGLE_LABEL_OFFSET_PX;
  const signedAreaTwice = input.screenPoints.reduce((sum, point, index) => {
    const next = input.screenPoints[(index + 1) % input.screenPoints.length];
    return next ? sum + point[0] * next[1] - next[0] * point[1] : sum;
  }, 0);

  return input.metricPoints.flatMap((_, pointIndex) => {
    const angleDegrees = getPolygonCornerAngleDegrees(
      input.metricPoints,
      pointIndex,
    );
    const vertex = input.screenPoints[pointIndex];
    const previous =
      input.screenPoints[
        (pointIndex - 1 + input.screenPoints.length) % input.screenPoints.length
      ];
    const next =
      input.screenPoints[(pointIndex + 1) % input.screenPoints.length];
    if (angleDegrees === undefined || !vertex || !previous || !next) return [];

    const getOutwardNormal = (start: Point2D, end: Point2D): Point2D => {
      const dx = end[0] - start[0];
      const dy = end[1] - start[1];
      const length = Math.hypot(dx, dy);
      if (!(length > 1e-6)) return [0, 0];
      return signedAreaTwice > 0
        ? [dy / length, -dx / length]
        : [-dy / length, dx / length];
    };
    const incomingNormal = getOutwardNormal(previous, vertex);
    const outgoingNormal = getOutwardNormal(vertex, next);
    let outwardX = incomingNormal[0] + outgoingNormal[0];
    let outwardY = incomingNormal[1] + outgoingNormal[1];
    const outwardLength = Math.hypot(outwardX, outwardY);
    if (outwardLength > 1e-6) {
      outwardX /= outwardLength;
      outwardY /= outwardLength;
    } else {
      outwardX = outgoingNormal[0];
      outwardY = outgoingNormal[1];
    }
    return [
      {
        pointIndex,
        angleDegrees,
        screenPoint: [
          vertex[0] + outwardX * offsetPx,
          vertex[1] + outwardY * offsetPx,
        ],
      },
    ];
  });
};
